// ProfileCard.js — Carte/popup PROFIL d'un joueur (sans redirection de page).
// Affiche niveau, statut, barre d'XP, stats (matches/victoires…), jeux joués et badges.
// Alimentée par la progression PUBLIQUE (auth.getUserProgress) + les infos du résultat de recherche.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiX, FiUserPlus, FiPlay, FiLock } from 'react-icons/fi';
import { theme } from '../theme';
import { pctFromBounds } from '../services/xpConfig';
import { useAuth } from '../context/AuthContext';
import { labelFromStatus, vgFromStatus, modeLabel } from '../services/activity';
import { AvatarInner } from './HeroAvatar';

const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 2000; display: flex; align-items: center; justify-content: center;
  background: rgba(0,0,0,.6); padding: 1rem;
`;
const Card = styled.div`
  width: min(420px, 100%); max-height: 90vh; overflow-y: auto;
  background: ${theme.colors.bgCard}; border: 1px solid ${theme.colors.borderHover};
  border-radius: 16px; box-shadow: ${theme.shadows.card}; color: ${theme.colors.textPrimary};
`;
const Head = styled.div`
  display: flex; align-items: center; gap: .8rem; padding: 1.1rem 1.1rem .8rem;
  position: relative;
  .av { width: 56px; height: 56px; border-radius: 50%; background: ${theme.colors.gradient};
        display: flex; align-items: center; justify-content: center; color: #fff; font-weight: 800; font-size: 1.2rem; flex-shrink: 0; overflow: hidden; }
  .av img { width: 100%; height: 100%; object-fit: cover; }
  .n { font-family: ${theme.fonts.heading}; font-size: 1.05rem; font-weight: 800; }
  .u { color: ${theme.colors.textMuted}; font-size: .8rem; }
  .close { position: absolute; top: .6rem; right: .6rem; background: none; border: none; color: ${theme.colors.textMuted}; cursor: pointer; font-size: 1.1rem; display: flex; }
`;
const Lvl = styled.div`
  margin: 0 1.1rem .6rem; padding: .6rem .8rem; border-radius: 12px;
  background: rgba(124,58,237,.12); border: 1px solid rgba(124,58,237,.28);
  .row { display: flex; align-items: center; justify-content: space-between; font-size: .82rem; }
  .big { font-weight: 800; color: ${theme.colors.primaryLight}; }
  .bar { height: 8px; border-radius: 999px; background: rgba(255,255,255,.08); margin-top: .45rem; overflow: hidden; }
  .fill { height: 100%; background: ${theme.colors.gradient}; }
  .xp { font-size: .72rem; color: ${theme.colors.textMuted}; margin-top: .3rem; text-align: right; }
`;
const Stats = styled.div`
  display: grid; grid-template-columns: repeat(4, 1fr); gap: .5rem; margin: 0 1.1rem .7rem;
  .s { background: ${theme.colors.bgPrimary}; border: 1px solid ${theme.colors.border}; border-radius: 10px; padding: .5rem; text-align: center; }
  .v { font-weight: 800; font-size: 1rem; }
  .k { font-size: .66rem; color: ${theme.colors.textMuted}; text-transform: uppercase; letter-spacing: .04em; }
`;
const Sec = styled.div`
  margin: 0 1.1rem .8rem;
  .t { font-size: .72rem; color: ${theme.colors.textMuted}; text-transform: uppercase; letter-spacing: .06em; margin-bottom: .4rem; }
  .chips { display: flex; flex-wrap: wrap; gap: .35rem; }
  .chip { background: ${theme.colors.bgPrimary}; border: 1px solid ${theme.colors.border}; border-radius: 999px; padding: .25rem .6rem; font-size: .76rem; }
`;
const Foot = styled.div`padding: 0 1.1rem 1.1rem; display: flex; gap: .5rem;`;
const Btn = styled.button`
  flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: .4rem;
  border: none; cursor: pointer; border-radius: 999px; padding: .6rem; font-weight: 700; font-size: .85rem;
  background: ${theme.colors.gradient}; color: #fff;
  &:disabled { opacity: .5; cursor: default; }
`;
const Muted = styled.p`color: ${theme.colors.textMuted}; font-size: .82rem; text-align: center; padding: .4rem 1.1rem 1rem;`;

// Libellés lisibles des déblocages (badges/cosmétiques/gameplay).
const UNLOCK_LABEL = {
  badge_bronze: '🥉 Badge Bronze', title_or: '👑 Titre Or', extra_crate_slot: '📦 Caisse bonus',
  special_weapon_1: '🔫 Arme spéciale', special_ability_1: '⚡ Capacité spéciale',
};
function unlockLabel(id) {
  const s = String(id || '');
  if (UNLOCK_LABEL[s]) return UNLOCK_LABEL[s];
  if (/^skin_.*legendary$/.test(s)) return '🌟 Skin légendaire';
  if (/^skin_/.test(s)) return '🎨 Skin';
  return s;
}
// Aplatit les déblocages en un tableau de CHAÎNES (les valeurs objet/non-string sont ignorées → jamais rendu-objet).
function allUnlocks(u) {
  if (!u) return [];
  const arr = Array.isArray(u) ? u : [].concat(u.cosmetic || [], u.gameplay || []);
  return arr.filter((x) => typeof x === 'string');
}

export default function ProfileCard({ user, onClose, onInvite, isFriend, activity }) {
  const { auth } = useAuth();
  const [prog, setProg] = useState(undefined); // undefined = chargement, null = pas de données
  const uid = user && (user.user_id || user.id);
  const name = (user && (user.display_name || user.username)) || 'Joueur';

  useEffect(() => {
    let alive = true;
    if (uid) auth.getUserProgress(uid).then((p) => { if (alive) setProg(p || null); });
    return () => { alive = false; };
  }, [uid, auth]);

  // Statut LIVE de l'ami (activité + état de partie `vg`) tant que la carte est ouverte : on suit sa présence
  // et on rafraîchit. Permet de VOIR sa partie en cours (jeu, mode, public/privé, joueurs) et de la REJOINDRE.
  const navigate = useNavigate();
  const [live, setLive] = useState({ act: activity || null, vg: null });
  useEffect(() => {
    if (!uid) return undefined;
    let alive = true;
    const read = async () => {
      try {
        const pres = await auth.followUsers([uid]);
        const p = (pres || [])[0];
        if (!alive) return;
        if (p) setLive({ act: labelFromStatus(p.status) || 'En ligne', vg: vgFromStatus(p.status) });
        else setLive({ act: null, vg: null });   // hors ligne
      } catch (e) { /* */ }
    };
    read();
    const off = auth.onStatusPresence((ev) => {
      const j = (ev.joins || []).find((x) => x.user_id === uid);
      if (j) { setLive({ act: labelFromStatus(j.status) || 'En ligne', vg: vgFromStatus(j.status) }); return; }
      if ((ev.leaves || []).some((x) => x.user_id === uid)) setLive({ act: null, vg: null });
    });
    const iv = setInterval(read, 8000);
    return () => { alive = false; if (off) off(); clearInterval(iv); };
  }, [uid, auth]);
  const act = live.act || activity;
  const vg = live.vg;
  const joinGame = () => { if (vg && vg.mid) { onClose && onClose(); navigate(`/salons?join=${encodeURIComponent(vg.mid)}${vg.slug ? `&jeu=${encodeURIComponent(vg.slug)}` : ''}`); } };

  const pct = prog ? pctFromBounds(prog.xp, prog.xpForCurrent, prog.xpForNext) : 0;
  const unlocks = allUnlocks(prog && prog.unlocks);
  const perGame = (prog && prog.perGame) || {};
  const games = Object.keys(perGame);

  return (
    <Overlay onClick={onClose}>
      <Card onClick={(e) => e.stopPropagation()}>
        <Head>
          <span className="av"><AvatarInner url={user && user.avatar_url} name={name} size={56} /></span>
          <div>
            <div className="n">{name}</div>
            <div className="u">@{(user && user.username) || '—'}</div>
            {act && <div style={{ marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '.76rem', fontWeight: 700, color: '#22c55e' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 5px #22c55e' }} />{act}
            </div>}
          </div>
          <button className="close" onClick={onClose} aria-label="Fermer"><FiX /></button>
        </Head>

        {/* Partie EN DIRECT de l'ami : jeu, mode, public/privé, joueurs, phase → bouton Rejoindre (il t'amène
            dans le salon). Permet de savoir ce qu'il fait AVANT de le rejoindre. */}
        {vg && vg.mid && (
          <div style={{ border: '1px solid rgba(124,58,237,.4)', background: 'rgba(124,58,237,.08)', borderRadius: theme.radii.md, padding: '.7rem .8rem', margin: '.2rem 0 .8rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.4rem', flexWrap: 'wrap', marginBottom: '.4rem' }}>
              <span style={{ fontWeight: 800, color: theme.colors.primaryLight }}>{vg.phase === 'ingame' ? '🎮 En partie' : '🕒 Salon'}</span>
              <span style={{ color: theme.colors.textPrimary, fontWeight: 700 }}>{vg.game}</span>
              {vg.priv
                ? <span title="Salon privé (amis)" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '.72rem', color: '#f7a83c' }}><FiLock /> privé</span>
                : <span style={{ fontSize: '.72rem', color: '#22c55e' }}>● public</span>}
            </div>
            <div style={{ display: 'flex', gap: '.9rem', flexWrap: 'wrap', fontSize: '.8rem', color: theme.colors.textSecondary, marginBottom: '.6rem' }}>
              {vg.mode && <span>Mode : <b style={{ color: theme.colors.textPrimary }}>{modeLabel(vg.mode)}</b></span>}
              <span>Joueurs : <b style={{ color: theme.colors.textPrimary }}>{vg.count}/{vg.max}</b></span>
              {vg.phase !== 'ingame' && typeof vg.ready === 'number' && <span>Prêts : <b style={{ color: theme.colors.textPrimary }}>{vg.ready}/{vg.count}</b></span>}
            </div>
            {/* Détail LIVE de la partie en cours (relayé depuis le jeu) : tour + vers en vie & PV par équipe. */}
            {vg.gs && (
              <div style={{ borderTop: `1px solid ${theme.colors.border}`, paddingTop: '.5rem', marginBottom: '.6rem', fontSize: '.78rem' }}>
                {vg.gs.turn && <div style={{ marginBottom: '.35rem', color: theme.colors.textSecondary }}>🎯 Tour de <b style={{ color: theme.colors.textPrimary }}>{vg.gs.turn}</b></div>}
                {Array.isArray(vg.gs.teams) && vg.gs.teams.map((t, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '.5rem', padding: '.15rem 0', color: t.a > 0 ? theme.colors.textSecondary : theme.colors.textMuted }}>
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: t.a > 0 ? 'none' : 'line-through' }}>{t.n || 'Équipe'}</span>
                    <span title="Vers en vie">🪱 <b style={{ color: theme.colors.textPrimary }}>{t.a}</b></span>
                    <span title="Points de vie">❤️ <b style={{ color: theme.colors.textPrimary }}>{t.h}</b></span>
                  </div>
                ))}
              </div>
            )}
            <button onClick={joinGame} disabled={vg.count >= vg.max && vg.phase !== 'ingame'}
              style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: (vg.count >= vg.max && vg.phase !== 'ingame') ? 'default' : 'pointer', border: 'none', borderRadius: theme.radii.full, padding: '.55rem', fontWeight: 800, color: '#fff', background: theme.colors.gradient, opacity: (vg.count >= vg.max && vg.phase !== 'ingame') ? 0.5 : 1 }}>
              <FiPlay /> {vg.count >= vg.max && vg.phase !== 'ingame' ? 'Salon complet' : (vg.phase === 'ingame' ? 'Voir / Rejoindre la partie' : 'Rejoindre le salon')}
            </button>
          </div>
        )}

        {prog === undefined && <Muted>Chargement du profil…</Muted>}
        {prog === null && <Muted>Ce joueur n'a pas encore de progression.</Muted>}

        {prog && (
          <>
            <Lvl>
              <div className="row"><span className="big">Niveau {Number(prog.level) || 1}</span><span>{String(prog.status || '')}</span></div>
              <div className="bar"><div className="fill" style={{ width: `${pct}%` }} /></div>
              <div className="xp">{Number(prog.xp) || 0} XP · niveau {(Number(prog.level) || 1) + 1} à {Number(prog.xpForNext) || 0} XP</div>
            </Lvl>
            <Stats>
              <div className="s"><div className="v">{prog.matches || 0}</div><div className="k">Parties</div></div>
              <div className="s"><div className="v">{prog.wins || 0}</div><div className="k">Victoires</div></div>
              <div className="s"><div className="v">{prog.losses || 0}</div><div className="k">Défaites</div></div>
              <div className="s"><div className="v">{prog.draws || 0}</div><div className="k">Nuls</div></div>
            </Stats>
            {games.length > 0 && (
              <Sec>
                <div className="t">Jeux joués</div>
                <div className="chips">
                  {games.map((g) => {
                    const pg = perGame[g];
                    const cnt = pg && typeof pg === 'object' ? (pg.matches || pg.games || pg.count || 0) : (pg || 0);
                    return <span className="chip" key={g}>{g} · {Number(cnt) || 0}</span>;
                  })}
                </div>
              </Sec>
            )}
            <Sec>
              <div className="t">Badges & déblocages</div>
              <div className="chips">
                {unlocks.length > 0 ? unlocks.map((u) => <span className="chip" key={u}>{unlockLabel(u)}</span>)
                  : <span className="chip" style={{ opacity: .7 }}>Aucun badge pour l'instant</span>}
              </div>
            </Sec>
          </>
        )}

        {onInvite && uid !== (auth.session && auth.session.user_id) && (
          <Foot>
            <Btn onClick={() => onInvite(user)} disabled={isFriend}>
              <FiUserPlus /> {isFriend ? 'Déjà ami' : 'Inviter en ami'}
            </Btn>
          </Foot>
        )}
      </Card>
    </Overlay>
  );
}
